/**
 * 🔥 SERVICE FIREBASE ULTRA-SIMPLE
 * 
 * Une seule responsabilité : récupérer les données Firebase
 * Sans complexité, sans promesses inutiles, sans bugs
 */

import { database } from '../firebase';
import { ref, onValue, push, update, remove } from 'firebase/database';

// =============================================
// 📋 ÉCOUTER LES DEMANDES EN ATTENTE
// =============================================
export function listenToWaitingRequests(callback) {
  console.log('🚀 [simpleFirebase] Écoute des demandes en attente...');
  
  // Protection absolue
  if (typeof callback !== 'function') {
    console.error('❌ [simpleFirebase] Callback invalide !');
    return () => {};
  }

  try {
    const requestsRef = ref(database, 'requests');
    
    const unsubscribe = onValue(requestsRef, (snapshot) => {
      console.log('📡 [simpleFirebase] Snapshot reçu !');
      
      try {
        if (!snapshot.exists()) {
          console.log('📭 [simpleFirebase] Aucune donnée dans Firebase');
          callback([]);
          return;
        }

        const rawData = snapshot.val();
        console.log('📊 [simpleFirebase] Données brutes:', Object.keys(rawData).length, 'demandes');

        // Conversion en tableau avec filtre côté client
        const allRequests = Object.entries(rawData).map(([id, data]) => ({
          id,
          ...data
        }));

        // Filtrer seulement les demandes NON assignées
        const waitingRequests = allRequests.filter(request => {
          const isWaiting = !request.assigned; // false, undefined, null = en attente
          console.log(`🔍 [simpleFirebase] ${request.id}: assigned=${request.assigned} → waiting=${isWaiting}`);
          return isWaiting;
        });

        console.log(`✅ [simpleFirebase] Résultat: ${waitingRequests.length} demandes en attente (sur ${allRequests.length} total)`);
        callback(waitingRequests);

      } catch (error) {
        console.error('❌ [simpleFirebase] Erreur traitement snapshot:', error);
        callback([]);
      }
    }, (error) => {
      console.error('❌ [simpleFirebase] Erreur Firebase listener:', error);
      callback([]);
    });

    console.log('✅ [simpleFirebase] Listener installé');
    return unsubscribe;

  } catch (error) {
    console.error('❌ [simpleFirebase] Erreur setup listener:', error);
    return () => {};
  }
}

// =============================================
// 📋 ÉCOUTER LES DEMANDES ACCEPTÉES
// =============================================
export function listenToAcceptedRequests(callback) {
  console.log('🚀 [simpleFirebase] Écoute des demandes acceptées...');
  
  if (typeof callback !== 'function') {
    console.error('❌ [simpleFirebase] Callback invalide !');
    return () => {};
  }

  try {
    const requestsRef = ref(database, 'requests');
    
    const unsubscribe = onValue(requestsRef, (snapshot) => {
      console.log('📡 [simpleFirebase] Snapshot acceptées reçu !');
      
      try {
        if (!snapshot.exists()) {
          callback([]);
          return;
        }

        const rawData = snapshot.val();
        const allRequests = Object.entries(rawData).map(([id, data]) => ({
          id,
          ...data
        }));

        // Filtrer les demandes assignées ET les demandes partiellement remplies (avec au moins un conducteur)
        const acceptedRequests = allRequests.filter(request => {
          const isAssigned = request.assigned === true;
          const seatsAssigned = Number(request.seatsAssigned || 0);
          const hasDrivers = request.drivers && Object.keys(request.drivers).length > 0;
          const hasSeatsAssigned = seatsAssigned > 0;
          
          const shouldInclude = isAssigned || (hasDrivers && hasSeatsAssigned);
          
          if (shouldInclude && hasDrivers) {
            const driverNames = Object.keys(request.drivers);
            const driverDetails = driverNames.map(name => {
              const driverData = request.drivers[name];
              return `${name}(${driverData?.seatsTaken || 1})`;
            }).join(', ');
            console.log(`✅ [simpleFirebase] Demande acceptée: ${request.id} (${request.name || request.passengerName}), drivers: [${driverDetails}], total: ${seatsAssigned}/${request.seatsNeeded || 1}`);
          }
          
          return shouldInclude;
        });

        console.log(`✅ [simpleFirebase] ${acceptedRequests.length} demandes acceptées`);
        callback(acceptedRequests);

      } catch (error) {
        console.error('❌ [simpleFirebase] Erreur traitement acceptées:', error);
        callback([]);
      }
    }, (error) => {
      console.error('❌ [simpleFirebase] Erreur Firebase listener acceptées:', error);
      callback([]);
    });

    return unsubscribe;

  } catch (error) {
    console.error('❌ [simpleFirebase] Erreur setup listener acceptées:', error);
    return () => {};
  }
}

// =============================================
// ✅ ACCEPTER UNE DEMANDE
// =============================================
export async function acceptRequest(requestId, driverName) {
  console.log(`🚗 [simpleFirebase] Acceptation demande ${requestId} par ${driverName}`);
  
  try {
    const requestRef = ref(database, `requests/${requestId}`);
    
    await update(requestRef, {
      assigned: true,
      assignedTo: driverName,
      assignedAt: Date.now()
    });

    console.log(`✅ [simpleFirebase] Demande ${requestId} acceptée !`);
    return { success: true };

  } catch (error) {
    console.error('❌ [simpleFirebase] Erreur acceptation:', error);
    return { success: false, error: error.message };
  }
}

// =============================================
// 🗑️ SUPPRIMER UNE DEMANDE
// =============================================
export async function deleteRequest(requestId) {
  console.log(`🗑️ [simpleFirebase] Suppression demande ${requestId}`);
  
  try {
    const requestRef = ref(database, `requests/${requestId}`);
    await remove(requestRef);

    console.log(`✅ [simpleFirebase] Demande ${requestId} supprimée !`);
    return { success: true };

  } catch (error) {
    console.error('❌ [simpleFirebase] Erreur suppression:', error);
    return { success: false, error: error.message };
  }
}

// =============================================
// ➕ CRÉER UNE NOUVELLE DEMANDE
// =============================================
export async function createRequest(requestData) {
  console.log('➕ [simpleFirebase] Création nouvelle demande:', requestData.name);
  
  try {
    const requestsRef = ref(database, 'requests');
    
    const newRequestData = {
      ...requestData,
      assigned: false, // EXPLICITEMENT false
      createdAt: Date.now()
    };

    const newRef = await push(requestsRef, newRequestData);

    console.log(`✅ [simpleFirebase] Demande créée avec ID: ${newRef.key}`);
    return { success: true, id: newRef.key };

  } catch (error) {
    console.error('❌ [simpleFirebase] Erreur création:', error);
    return { success: false, error: error.message };
  }
}

